/**
 * Per-call request deadlines for the dashboard API client.
 *
 * `fetchApi` / `fetchApiEnvelope` called `fetch()` with no timeout, no
 * AbortController and no signal, so a request that STALLED rather than failed
 * never settled: any caller gating UI on the promise waited forever, and
 * because a pending request emits nothing, analytics could not see it either.
 *
 * Measured on production 2026-08-12: user-0fa99b@example.com created a client
 * review at 07:47:04, loaded /review-handoff/8876dfde... at 07:48:17 (which
 * POSTs /api/review/:token/handoff), and 19.2s later was back on her issues
 * page. PostHog recorded ZERO `api_network_failure` and ZERO `$exception` in
 * that window, and there is no `review_page_viewed` for that token ever. The
 * request never failed; it was still pending. She never reached the review she
 * had made 73 seconds earlier. (GitHub issue #75.)
 *
 * The bound is OPT-IN per call and defaults to off. A single global ceiling
 * safe for both an AI analysis call and a POST that does three indexed queries
 * does not exist, so adoption is caller-by-caller, starting with the requests
 * that gate a whole screen. See `auth.ts` and `clientReviews.ts`.
 *
 * Two things a naive `AbortController` + `setTimeout` gets wrong, and why this
 * module exists rather than the four lines inline:
 *
 *  1. **A deadline abort and a caller abort are different events.**
 *     `networkError.ts` deliberately passes AbortError through untouched so
 *     component unmount and query cancellation keep their meaning. If a
 *     deadline surfaced as a bare AbortError too, the user would read the
 *     browser's cancellation jargon in a form ("signal is aborted without
 *     reason"), which is the exact class of bug networkError.ts exists to
 *     prevent. `didTimeout()` is how the client tells the two apart.
 *  2. **A caller may already pass its own `signal`** (FetchOptions extends
 *     RequestInit). Overwriting it would silently disable that cancellation, so
 *     the two are composed. `AbortSignal.any()` would do this in one line but
 *     is not in every browser we support, and composing by hand keeps the
 *     module testable under jsdom.
 *
 * Pure module: no `import.meta.env`, no logger, no posthog, so it loads under
 * jest. `client.ts` itself cannot (module-scope `import.meta.env`), which is why
 * the wiring is pinned by a source-level guard in `__tests__/client.timeout.test.ts`.
 * Same pattern as networkError.ts / networkFailure.ts / sessionExpired.ts.
 */

/**
 * Used by LoadError for a typed TimeoutError and by forms that show `error.message`,
 * so it says what happened and what to do, and never mentions abort or fetch.
 */
export const REQUEST_TIMEOUT_MESSAGE =
  'The server took too long to respond. Check your connection and try again.';

/**
 * Upper bound for a plain indexed list read that gates a WHOLE SCREEN.
 *
 * One constant rather than one per call site because these are one class, not
 * three decisions: `ticketsApi.list` (IssuesPage, TicketsPage),
 * `projectsApi.list` (ProjectsPage) and `scheduledScansApi.list` (SchedulePage)
 * are each the single request their page renders a skeleton for and an error
 * card from, and each is a handful of indexed queries with no external call.
 * Splitting them into three numbers would invent three judgements where the
 * evidence supports one.
 *
 * 15s, derived from the two calls it sits between rather than picked:
 *
 *  - Not below 12s (`HANDOFF_REQUEST_TIMEOUT_MS`), which bounds a POST doing
 *    the same kind of work. A read has no reason to be stricter than that.
 *  - Not above 20s (`BOOTSTRAP_TIMEOUT_MS`). Bootstrap carries Railway
 *    cold-start headroom these do not need: they only run once the shell is
 *    already up, so the cold start has by definition already been paid.
 *  - Under ~19.2s, where the 2026-08-12 user abandoned. A bound above the point
 *    people give up never reaches anyone.
 *
 * This is NOT a general ceiling and must not become one. AI analysis,
 * Puppeteer-backed scans and bulk creates stay unbounded: a number safe for a
 * GPT-4o vision call is useless to a list query, and one tight enough for a
 * list query aborts real AI work. Issue #75 asks for one number per latency
 * class, not one number.
 *
 * A bounded request is deliberately NOT retried (`shouldRetryApiQuery`), so 15s
 * is the real ceiling rather than 30s plus backoff. The retry is the user's:
 * every one of these screens already renders a LoadError with a Try again, so
 * the trade is an infinite skeleton for an error card with a button.
 */
export const SCREEN_READ_TIMEOUT_MS = 15_000;

/**
 * Upper bound for a plain MUTATION: a POST/PATCH/DELETE whose server work is
 * indexed Postgres writes, a small upload, or one short provider call (a Slack
 * post, an invite email). Adopted per call site, and a call site that is NOT
 * bounded must be listed with its reason in the derived guard
 * (`__tests__/apiRequestDeadlines.source.test.ts`), the same contract reads
 * carry. Emily's call, 2026-09-22: one generous default with explicit opt-outs,
 * rather than a second per-class constant or leaving mutations unbounded.
 *
 * 60s, derived from what it sits between rather than picked:
 *
 *  - Over 40x the worst PLAIN create measured on the real app: the 2026-08-23
 *    issue-create latency run read `POST /api/issues` at 311-1337ms across six
 *    creates, mean 587ms (docs/test-reports/2026-08-23-issue-create-latency/).
 *    A bounded request has never been measured within an order of magnitude
 *    of this number.
 *  - Above the 30s ceiling every EXTENSION request already lives under
 *    (`API_PROXY_TIMEOUT_MS`, apiProxy.ts), so the dashboard is the more
 *    lenient client, never the stricter one.
 *  - Below nothing that is bounded, by construction: the classes that can run
 *    longer are EXEMPT by name, not squeezed under it. An export chunk is
 *    measured at up to ~46s (EXPORT_CLIENT_CHUNK_SIZE work, 2026-09-11), an AI
 *    analysis or a headless-browser capture runs for minutes, and each of
 *    those keeps its own server-side ceiling.
 *
 * A mutation that hits this bound surfaces through the form's own
 * `error.message` as REQUEST_TIMEOUT_MESSAGE; the retry is the user's. A
 * create that times out may still land server-side, which is why the comments
 * and issues POSTs carry an Idempotency-Key and why the guard's exempt list
 * names every unkeyed create it knowingly bounds.
 */
export const MUTATION_TIMEOUT_MS = 60_000;

/**
 * A request that hit its own deadline. Distinct from `NetworkError` (fetch
 * rejected without a usable response) and from `ApiError` (the server answered):
 * this one means we stopped waiting. Subclasses Error so existing
 * `instanceof Error` handling is unchanged.
 */
export class TimeoutError extends Error {
  /** The bound that fired, so a report can say whose deadline it was. */
  readonly timeoutMs: number;

  constructor(timeoutMs: number, message: string = REQUEST_TIMEOUT_MESSAGE) {
    super(message);
    this.name = 'TimeoutError';
    this.timeoutMs = timeoutMs;
  }
}

export function isTimeoutError(err: unknown): err is TimeoutError {
  return err instanceof TimeoutError;
}

export interface TimeoutHandle {
  /** Pass to `fetch()`. Aborts on OUR deadline or on the caller's own signal. */
  signal: AbortSignal;
  /**
   * The bound actually armed. The handle is the authority on it, so the client
   * can build a TimeoutError without re-deriving (or casting) the caller's
   * possibly-undefined option.
   */
  timeoutMs: number;
  /**
   * True only when OUR deadline fired. A caller abort leaves this false so the
   * AbortError passes through untouched and cancellation keeps its semantics.
   */
  didTimeout: () => boolean;
  /** Clear the timer and the caller-signal listener. Idempotent. */
  cleanup: () => void;
}

/**
 * Arm a deadline for one request, composed with the caller's own signal.
 *
 * Returns `null` when no usable bound was asked for, which is the default and
 * keeps today's unbounded behaviour byte-for-byte. A non-finite or non-positive
 * bound is treated as "no bound" rather than aborting instantly: a bad value
 * arriving from config should degrade to the old behaviour, not break every
 * request that reads it.
 */
export function startRequestTimeout(
  timeoutMs: number | undefined,
  callerSignal?: AbortSignal | null,
): TimeoutHandle | null {
  if (typeof timeoutMs !== 'number' || !Number.isFinite(timeoutMs) || timeoutMs <= 0) {
    return null;
  }

  const controller = new AbortController();
  let timedOut = false;

  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  const onCallerAbort = () => controller.abort();
  if (callerSignal) {
    if (callerSignal.aborted) {
      controller.abort();
    } else {
      callerSignal.addEventListener('abort', onCallerAbort, { once: true });
    }
  }

  let cleaned = false;
  return {
    signal: controller.signal,
    timeoutMs,
    didTimeout: () => timedOut,
    cleanup: () => {
      if (cleaned) return;
      cleaned = true;
      clearTimeout(timer);
      callerSignal?.removeEventListener('abort', onCallerAbort);
    },
  };
}
